// @flow
import React from 'react';
import cx from 'classnames';
import dataFormat from 'date-fns/format';
import Button from 'components/common/Button';
import { imageURL, parseArrayString, orderShippingStatus, translationKeys, numberWithCommas } from "lib/common";
import { Link } from 'react-router-dom';
import { camelCase, kebabCase, get } from 'lodash';
import './OrderDetailTemplate.scss';

type Props = {
  order: any,
  goProduct: any,
  goInvoice: any,
  goOffer: any,
  goTax: any,
  goRefund: any,
  goNicePayReceipt: any,
  goTossPayReceipt: any,
  goPayJoaReceipt: any,
  onCheckOut: any,
  onCancelOrder: any,
  onPaymentMethod: any,
  cancelOrder: boolean,
  handleChange: any,
};

const bankCodes = {
  "39": "경남은행",
  "34": "광주은행",
  "12": "단위농협(지역농축협)",
  "32": "부산은행",
  "45": "새마을금고",
  "64": "산림조합",
  "88": "신한은행",
  "48": "신협",
  "27": "씨티은행",
  "20": "우리은행",
  "71": "우체국예금보험",
  "50": "저축은행중앙회",
  "37": "전북은행",
  "35": "제주은행",
  "90": "카카오뱅크",
  "89": "케이뱅크",
  "92": "토스뱅크",
  "81": "하나은행",
  "54": "홍콩상하이은행",
  "03": "IBK기업은행",
  "04": "KB국민은행",
  "31": "iM뱅크(대구)",
  "02": "한국산업은행",
  "11": "NH농협은행",
  "23": "SC제일은행",
  "07": "Sh수협은행"
};

function getBankName(code) {
  return bankCodes[code] || "알 수 없는 코드";
}

const statusTrans = (key) => {
  let words = {
    statusPlaced:                    '결제 대기',
    statusCancelledByStore:          '운영자 취소',
    statusCancelledByCustomer:       '고객 취소',
    statusPaid:                      '결제 완료',
    statusUnderPaid:                 '부분 결제',
    statusOverPaid:                  '초과 결제',
    statusRefunded:                  '환불 완료',
    statusPartiallyRefunded:         '부분 환불 완료',
    statusUnderRefunded:             '환불 처리 중',
    statusOverRefunded:              '초과 환불',
    shippingStatusPending:           '배송 대기',
    shippingStatusShipped:           '배송중',
    shippingStatusArrived:           '배송 완료',
    shippingStatusReceived:          '수령 완료',
    refundStatusRequested:           '승인 대기',
    refundStatusCancelledByStore:    '운영자 취소',
    refundStatusCancelledByCustomer: '고객 취소',
    refundStatusAccepted:            '환불 승인',
    orderTotalPaid:                  '결제 금액',
    orderTotalCancelled:             '결제 취소 금액',
    orderTotalRefunded:              '그 외 환불 금액',
    customerFirstName:               '주문자 이름',
    customerLastName:                '주문자 성',
    customerFullName:                '주문자명',
    customerEmail:                   '이메일',
    customerMobile:                  '핸드폰 번호',
    customerPhone:                   '사업자 등록 번호',
    orderShippingAddress:            '배송지 주소',
    addressFirstName:                '수취자 이름',
    addressLastName:                 '수취자 성',
    addressFullName:                 '수취자명',
    addressMobile:                   '핸드폰 번호',
    addressPhone:                    '사업자 등록 번호',
    fulfillmentStatusPending:        '배송 대기',
    fulfillmentStatusShipped:        '배송중',
    fulfillmentStatusArrived:        '배송 완료',
    // naverPay:                        '네이버페이',
    niceByCard:                      '신용카드',
    virtualBank:                     '가상계좌',
  };
  return words[key];
};

const customerFields = parseArrayString(
  ['name.full:required', 'email:required', 'mobile:required', 'phone'].map(field => field.split(':')[0])
).map(key => ({
  key,
  translationKey: camelCase(['customer', translationKeys[key] || key]),
}));

const recipientFields = parseArrayString(
  ['name.full:required', 'mobile:required', 'phone'].map(field => field.split(':')[0])
).map(key => ({
  key,
  translationKey: camelCase(['address', translationKeys[key] || key]),
}));

const refundTotalDetail = total => [
  typeof total.items.price.withTax.raw === 'number' ?
    `상품 환불액: ${total.items.price.withTax.raw}원` :
    null,
  typeof total.shipping.fee.withTax.raw === 'number' ?
    `배송비 환불액: ${total.shipping.fee.withTax.raw}원` :
    null,
].filter(v => v).join(' + ');

const hasTangibleItem = allItems => allItems.some(item => item.type === 'tangible');

const fulfillmentTracker = ({ tracking }) => {

  const message = [
    tracking.company,
    tracking.uid ? `(${tracking.uid})` : null,
  ].filter(v => v).join(' ');

  return tracking.url ?
    <div>
      <a href={tracking.url} target="_blank">{message || '배송 조회'}</a>
    </div> :
    <div>
      <span>{message}</span>
    </div>;
};

const paymentMethods = [
  // {
  //   id: 'naverpay',
  //   label: 'naverPay',
  //   value: '네이버페이',
  //   cardFields: false,
  //   payLater: false,
  // },
  {
    id: 'iamport',
    label: 'niceByCard',
    cardFields: false,
    payLater: false,
    meta: {
      pg: 'nice',
      payMethod: 'card',
    },
  },
  {
    id: 'virtual-bank',
    label: 'virtualBank',
    cardFields: false,
    payLater: false,
    meta: {
      pg: 'nice',
      payMethod: 'vbank'
    }
  },
];

const paymentMethodValue = [
  {
    id: 'naverpay',
    label: 'naverPay',
    value: '네이버페이',
    cardFields: false,
    payLater: false,
  },
  {
    id: 'tosspay',
    label: 'tossPay',
    value: '토스페이',
    cardFields: false,
    payLater: false,
  },
  {
    id: 'kakaopay',
    label: 'kakaoPay',
    value: '카카오페이',
    cardFields: false,
    payLater: false,
  },
  {
    id: 'iamport',
    label: 'niceByCard',
    value: '신용카드',
    cardFields: false,
    payLater: false,
    meta: {
      pg: 'nice',
      payMethod: 'card',
    },
  },
  {
    id: 'clayful-iamport',
    label: 'niceByCard',
    value: '신용카드',
    cardFields: false,
    payLater: false,
    meta: {
      pg: 'nice',
      payMethod: 'card',
    },
  },
  {
    id: 'virtual-bank',
    label: 'virtualBank',
    value: '가상계좌',
    cardFields: false,
    payLater: false,
    meta: {
      pg: 'nice',
      payMethod: 'vbank',
    },
  },
  {
    id: 'research-transfer',
    label: 'researchTransfer',
    value: '연구비카드',
    cardFields: false,
    payLater: true,
  },
  {
    id: 'kb-bank-transfer',
    label: 'kbBankTransfer',
    value: '세금계산서 (신한은행 100-032-703829)',
    cardFields: false,
    payLater: true,
  },
  {
    id: 'bank-transfer',
    label: 'bankTransfer',
    value: '세금계산서 (기업은행 033-502993-01-019)',
    cardFields: false,
    payLater: true,
  },
  {
    id: 'post-transfer',
    label: 'postTransfer',
    value: '후불결제',
    cardFields: false,
    payLater: true,
  },
  {
    id: 'quick-bank-transfer',
    label: 'quickBankTransfer',
    value: '퀵계좌이체',
    cardFields: false,
    payLater: true,
  },
  {
    id: 'inventory-transfer',
    label: 'inventoryTransfer',
    value: '재고 확인 후 결제',
    cardFields: false,
    payLater: true,
  },
  {
    id: 'default',
    label: 'default',
    value: '디폴트',
    cardFields: false,
    payLater: true,
  },
];

const OrderDetailTemplate = (props: Props) => (
  <div className="OrderDetailTemplate">
    <div className="order-detail" data-order={props.order.id}>
      <h2 className="with-back-button">주문 내역</h2>
      <Link to={'/orders'} className="back-to-orders">목록으로 돌아가기</Link>

      <div className="order-primary-info">
        <div className="order-basic-info">
          <h3>기본 정보</h3>

          <div className="order-status-container">
            <span className="tag order-status" data-status={props.order.status}>
              {props.order.status === 'cancelled'
                ? statusTrans(camelCase(['status', props.order.status, 'by', props.order.cancellation.by]))
                : statusTrans(camelCase(['status', props.order.status]))}
            </span>
            {orderShippingStatus(props.order) && (
              <span className="tag order-shipping-status" data-status={orderShippingStatus(props.order)}>
                {statusTrans(camelCase(['shippingStatus', orderShippingStatus(props.order)]))}
              </span>
            )}
          </div>

          {!props.order.synced && (
            <p className="order-sync-failed">
              주문에 실패했습니다. 관리자에게 문의해주세요.
            </p>
          )}

          <table>
            <tbody>
              <tr className="order-id">
                <th className="data-label">주문 번호</th>
                <td className="data-value">{props.order.id}</td>
              </tr>
              {props.order.transactions[0] && props.order.transactions[0].paymentMethod.slug && (
              <tr className="order-id">
                <th className="data-label">결제 방식</th>
                <td className="data-value">{paymentMethodValue.find(doc => doc.id === props.order.transactions[0].paymentMethod.slug).value || '없음'}</td>
              </tr>
              )}
            </tbody>
          </table>

          <p className="order-created-at">{`${dataFormat(props.order.created_at, 'YYYY년 M월 D일 HH시 mm분')}에 주문`}</p>

          {props.order.cancellation && props.order.cancellation.reason && (
            <div className="order-cancellation">
              <h4>취소 내용</h4>
              <div className="order-cancellation-reason">
                {props.order.cancellation.reason}
              </div>
            </div>
          )}

          {props.order.request && (
            <div className="order-request">
              <h4>요청 사항</h4>
              <div className="order-request-body">{props.order.request}</div>
            </div>
          )}

          {!!props.order.transactions.length && (
            <div className="order-transaction-summary">
              <h4>금액 정보</h4>
              <table className="order-transaction-details">
                <tbody>
                  {(['paid', 'cancelled', 'refunded'].map((key) => (
                    <tr key={key} className={`total-${key}`}>
                      <th className="data-label">{statusTrans(camelCase(['orderTotal', key]))}</th>
                      <td className="data-value">{numberWithCommas(props.order.transactions[0][key].raw)}원</td>
                    </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}

          {!!props.order.tosspayments && !!props.order.tosspayments.virtualAccount &&
            <div className="order-vbanks">
              <h4>
                가상 계좌 정보
              </h4>
              <table className="vbank-info">
                <tbody>
                  <tr className="vbank-name">
                    <th className="data-label">은행</th>
                    <td className="data-value">{getBankName(props.order.tosspayments.virtualAccount.bankCode)}</td>
                  </tr>
                  <tr className="vbank-number">
                    <th className="data-label">계좌번호</th>
                    <td className="data-value">
                      {props.order.tosspayments.virtualAccount.accountNumber}
                    </td>
                  </tr>
                  <tr className="vbank-amount">
                    <th className="data-label">입금 금액</th>
                    <td className="data-value">{numberWithCommas(props.order.tosspayments.totalAmount)}원</td>
                  </tr>
                  <tr className="vbank-expires-at">
                    <th className="data-label">입금 기한</th>
                    <td className="data-value">{dataFormat(props.order.tosspayments.virtualAccount.dueDate, 'YYYY년 M월 D일 HH시 mm분')}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          }

          {!!props.order.transactions.length && !!props.order.transactions[0].vbanks.length &&
            <div className="order-vbanks">
              <h4>
                가상 계좌 정보
              </h4>
              <table className="vbank-info">
                <tbody>
                  <tr className="vbank-name">
                    <th className="data-label">은행</th>
                    <td className="data-value">{props.order.transactions[0].vbanks[0].name}</td>
                  </tr>
                  <tr className="vbank-number">
                    <th className="data-label">계좌번호</th>
                    <td className="data-value">
                      {props.order.transactions[0].vbanks[0].number}
                    </td>
                  </tr>
                  <tr className="vbank-amount">
                    <th className="data-label">입금 금액</th>
                    <td className="data-value">{props.order.transactions[0].vbanks[0].amount.converted}</td>
                  </tr>
                  <tr className="vbank-expires-at">
                    <th className="data-label">입금 기한</th>
                    <td className="data-value">{props.order.transactions[0].vbanks[0].expiresAt.formatted}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          }

          {/* {props.order.status === 'placed'
            && (props.order.transactions[0].paymentMethod.slug === 'naverpay' ||
              props.order.transactions[0].paymentMethod.slug === 'iamport' ||
              props.order.transactions[0].paymentMethod.slug === 'virtual-bank'
            ) && (
            <div className="payment-container">
              <h4>주문 결제</h4>
              <div className="payment-form-container">
                <div className="payment-form form">

                  <div className="payment-method">
                    <label htmlFor="paymentMethod">결제 방식
                      <select name="paymentMethod" onChange={props.onPaymentMethod}>
                        <option disabled selected>결제 방식을 선택해주세요.</option>
                        {paymentMethods.map((paymentMethod, index) => (
                          <option key={paymentMethod.id} value={paymentMethod.id}>
                            {statusTrans(paymentMethod.label)}
                          </option>
                        ))}
                      </select>
                    </label>
                  </div>
                </div>
              </div>
              <Button className="make-payment button-small" onClick={props.onCheckOut}>결제하기</Button>
            </div>
          )} */}

          {props.order.status !== 'cancelled' && props.cancelOrder && (
            <form className="order-cancellation-form">
              <h4>취소 정보</h4>
              <textarea
                name="reason"
                value={props.reason}
                className="order-cancellation-reason"
                placeholder="취소 사유"
                onChange={props.handleChange}
              />
              <Button className="button-small" onClick={props.onCancel}>취소하기</Button>
            </form>
          )}

          <ul className="order-actions">
            {props.order.status === 'placed' && (
              <li>
                <span className="cancel-order" onClick={props.onCancelOrder}>주문 취소하기</span>
              </li>
            )}
          </ul>

          {(
            <div className="payment-container">
              <Button className="make-payment" color="green"  onClick={() => props.goOffer(props.order.id)}>견적서</Button>
              <Button className="make-payment" color="blue" onClick={() => props.goInvoice(props.order.id)}>거래명세서</Button>
              {!!props.order.meta.taxFile &&
                <Button className="make-payment" color="red" onClick={() => props.goTax(props.order.id)}>세금계산서</Button>
              }
              {!props.order.tosspayments && props.order.transactions[0].paymentMethod.slug === 'iamport' &&
                <Button className="make-payment" color="gray" onClick={props.goNicePayReceipt}>영수증</Button>
              }
              {!props.order.tosspayments && props.order.transactions[0].paymentMethod.slug === 'clayful-iamport' &&
                <Button className="make-payment" color="gray" onClick={props.goNicePayReceipt}>영수증</Button>
              }
              {!props.order.tosspayments && props.order.transactions[0].paymentMethod.slug === 'research-transfer' &&
                <Button className="make-payment" color="gray" onClick={props.goPayJoaReceipt}>영수증</Button>
              }
              {!!props.order.tosspayments && props.order.transactions[0].paymentMethod.slug &&
                <Button className="make-payment" color="gray" onClick={props.goTossPayReceipt}>영수증</Button>
              }
            </div>
          )}
        </div>

        <div className="order-item-details">
          <h3>주문 품목</h3>

          <table className="table cart-items">
            <thead>
              <tr>
                <th colSpan="2">상품 정보</th>
                <th>단가</th>
                <th>수량</th>
                <th>가격</th>
              </tr>
            </thead>
            <tbody>
              {props.order.items.map((item, index) => (
                <tr key={item.id} className="cart-item" data-item={item.id}>
                  <td className="cart-item-thumbnail-container">
                    <a
                      onClick={() => props.goProduct(item.product.id)}
                      className="cart-item-name-link"
                    >
                      <img
                        src={item.product ? imageURL(item.product.thumbnail, 120, 120) : ''}
                        className="cart-item-thumbnail"
                        alt="thumbnail"
                      />
                    </a>
                  </td>
                  <td className="cart-item-name">
                    <div className="cart-item-summary">
                      <h4 className="cart-item-name">
                        {item.product ?
                          <a
                            onClick={() => props.goProduct(item.product.id)}
                            className="cart-item-name-link"
                          >
                            {item.product.name}
                          </a> :
                          '삭제된 상품'
                        }
                      </h4>
                      <ul>
                        {item.product_price && (
                          <li className="cart-item-option">
                            <span className="cart-entity-label">카탈로그 번호</span>
                            {item.product_price.part_number}
                          </li>
                        )}
                        {item.product_price && (
                          <li className="cart-item-shipping-method">
                            <span className="cart-entity-label">설명</span>
                            {item.product_price.description}
                          </li>
                        )}
                        {item.product_price && (
                          <li className="cart-item-shipping-method">
                            <span className="cart-entity-label">단위</span>
                            {item.product_price.unit}
                          </li>
                        )}
                      </ul>
                    </div>
                  </td>
                  {item.price ? (
                    <td className="cart-item-unit-price">
                      <span className="cart-mobile-label">단가</span>
                      <span className="cart-item-sale-price">{numberWithCommas(item.price.sale.raw)}원</span>
                      {item.price.sale.raw !== item.price.original.raw && (
                        <span className="cart-item-compare-price">
                          {numberWithCommas(item.price.original.raw)}원
                        </span>
                      )}
                      {item.price.sale.raw !== item.price.original.raw && (
                        <span className="cart-item-discounted">(-{numberWithCommas(item.price.original.raw - item.price.sale.raw)}원)</span>
                      )}
                    </td>
                  ) : (
                    <td className="cart-item-unit-price">
                      <span className="cart-mobile-label">단가</span>
                      <span className="cart-item-sale-price">
                        {numberWithCommas(item.product_price.price)}원
                      </span>
                    </td>
                  )}
                  <td className="cart-item-quantity">
                    <span className="cart-mobile-label">수량</span>
                    <span className="cart-item-quantity-value">
                      {item.quantity}
                    </span>
                  </td>
                  <td className="cart-item-price">
                    <span className="cart-mobile-label">가격</span>
                    <span className="cart-item-sale-price">
                      {numberWithCommas(item.total.price.sale.raw)}원
                    </span>
                    {item.total.price.sale.raw !== item.total.price.original.raw && (
                      <div>
                        <span className="cart-item-compare-price">
                          {numberWithCommas(item.total.price.original.raw)}원
                        </span>
                        <span className="cart-item-discounted">{`(-${numberWithCommas(item.total.discounted.raw)}원)`}</span>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
            {props.order.appliedCoupon && (
              <tbody>
                <tr className="order-applied-coupon">
                  <td colSpan="4">
                    <span className="cart-entity-label">적용 쿠폰</span>
                    {props.order.appliedCoupon.name}
                    <span
                      data-balloon-break
                      data-balloon-pos="up"
                      data-balloon={props.couponDetail(props.order.appliedCoupon)}
                    >
                      <span className="question-mark"></span>
                    </span>
                  </td>
                </tr>
              </tbody>
            )}
            <tfoot>
              <tr className="item-total">
                <th className="total-label" colSpan="4">
                  상품 합계
                </th>
                <td className="total-value">
                  <span className="order-items-sale-price">
                    {numberWithCommas(props.order.total.price.sale.raw)}원
                  </span>
                  {props.order.total.price.sale.raw !==
                  props.order.total.price.original.raw && (
                    <div>
                      <span className="order-items-compare-price">
                        {numberWithCommas(props.order.total.price.original.raw)}원
                      </span>
                      <span className="order-items-discounted">{`-(${numberWithCommas(props.order.total.discounted.raw)}원)`}</span>
                    </div>
                  )}
                </td>
              </tr>
              <tr className="shipping-total">
                <th className="total-label" colSpan="4">
                  배송비 합계
                </th>
                <td className="total-value">
                  {numberWithCommas(props.order.total.shipping.fee.sale.raw)}원
                </td>
              </tr>
              <tr className="tax-total">
                <th className="total-label" colSpan="4">
                  부가세{' '}
                  <span className="tax-inlcuded">
                    ({'가격에 미포함'})
                  </span>
                </th>
                <td className="total-value">{numberWithCommas(props.order.total.taxed.raw)}원</td>
              </tr>
              <tr className="order-total">
                <th className="total-label" colSpan="4">
                  총계
                </th>
                <td className="total-value">{numberWithCommas(props.order.total.amount.raw)}원</td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      <div className="order-secondary-info">
        <div className="order-customer-and-address">
          <h3>고객 및 주소</h3>

          <div className="order-customer">
            <h4>주문 고객</h4>

            <table>
              <tbody>
                {customerFields.map((field, index) => (
                  <tr key={field.key} className={`order-${kebabCase(field.translationKey)}`}>
                    <th className="data-label">{statusTrans(field.translationKey)}</th>
                    <td className="data-value">
                      {get(props.order.customer, field.key) || ''}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="order-shipping-address">
            <h4>배송지 주소</h4>

            <table>
              <tbody>
                {recipientFields.map((field, index) => (
                  <tr key={field.key} className={`order-${kebabCase(field.translationKey)}`}>
                    <th className="data-label">{statusTrans(field.translationKey)}</th>
                    <td className="data-value">
                      {get(props.order.address.shipping, field.key) || ''}
                    </td>
                  </tr>
                ))}
                <tr className="address-state">
                  <th className="data-label">도</th>
                  <td className="data-value">
                    {get(props.order.address.shipping, 'state') || ''}
                  </td>
                </tr>
                <tr className="address-city">
                  <th className="data-label">시</th>
                  <td className="data-value">
                    {get(props.order.address.shipping, 'city') || ''}
                  </td>
                </tr>
                <tr className="address-address1">
                  <th className="data-label">주소</th>
                  <td className="data-value">
                    {get(props.order.address.shipping, 'address1') || ''}
                  </td>
                </tr>
                <tr className="address-address2">
                  <th className="data-label">상세주소</th>
                  <td className="data-value">
                    {get(props.order.address.shipping, 'address2') || ''}
                  </td>
                </tr>
                <tr className="address-postcode">
                  <th className="data-label">우편번호</th>
                  <td className="data-value">
                    {get(props.order.address.shipping, 'postcode') || ''}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        {props.order.status !== 'placed' && props.order.status !== 'cancelled' && (
          <div className="order-refunds">
            <h3>환불 내역</h3>

            <ul className="refund-actions">
              <li>
                <Button className="button-tiny request-refund" onClick={() => props.goRefund(props.order.id)}>환불 요청</Button>
              </li>
            </ul>

            {!props.order.refunds.length && (
              <p className="no-refunds">환불 내역이 없습니다.</p>
            )}

            {!!props.order.refunds.length && (
              <table className="table order-refund-list">
                <thead>
                  <tr>
                    <th>환불 상태</th>
                    <th>환불 품목</th>
                    <th>환불 금액</th>
                  </tr>
                </thead>

                <tbody>
                  {props.order.refunds.map((refund, index) => (
                    <tr key={refund._id} className="order-refund" data-refund={refund._id}>
                      <td>
                        <span
                          className="tag refund-status"
                          data-status={refund.status}
                        >
                          {refund.status === 'cancelled'
                            ? statusTrans(camelCase(['refundStatus', refund.status, 'by', refund.cancellation.by]))
                            : statusTrans(camelCase(['refundStatus', refund.status]))}
                        </span>
                      </td>
                      <td>
                        <ul className="refund-items">
                          {refund.items.map((item, i) => (
                            // eslint-disable-next-line react/no-array-index-key
                            <li key={`${item.id}-${i}`}>
                              <span className="refund-item-name">
                                {item.item.product.name}
                              </span>
                              <span className="refund-item-quantity">
                                {item.quantity.raw}
                              </span>
                            </li>
                          ))}
                        </ul>
                        {refund.status === 'requested' && (
                          <div className="refund-cancellation">
                            {refund.status === 'requested' && (
                              <div>
                                <Button
                                  className={cx('button-tiny', { hidden: props.isRefundCancellation })}
                                  onClick={props.changeRefundCancellation}
                                >
                                  환불 요청 취소
                                </Button>
                                <div className={cx('refund-cancellation-form', { hidden: !props.isRefundCancellation })}>
                                  <textarea
                                    name="reason"
                                    className="refund-cancellation-reason"
                                    placeholder="취소 사유"
                                    value={props.refundCancellationReason}
                                    onChange={props.changeRefundCancellationReason}
                                  />
                                  <Button
                                    className="button-tiny"
                                    onClick={() => props.onRefundCancellation(refund.id)}
                                  >
                                    환불 요청 취소
                                  </Button>
                                </div>
                              </div>
                            )}
                            {refund.status === 'cancelled' && refund.cancellation.reason && (
                              <span
                                className="refund-cancellation-reason"
                                data-balloon-length="large"
                                data-balloon-pos="up-left"
                                data-balloon={refund.cancellation.reason}
                              >환불 취소 사유
                                <span className="question-mark"></span>
                              </span>
                            )}
                          </div>
                        )}
                      </td>
                      <td>
                        <span className="refund-total-label hidden">환불 금액</span>
                        <span
                          className="refund-total"
                          data-balloon-length="large"
                          data-balloon-pos="up-left"
                          data-balloon={refundTotalDetail(refund.total)}
                        >
                          {numberWithCommas(refund.total.price.withTax.raw)}원
                          <span className="question-mark"></span>
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        {props.order.status !== 'placed' && props.order.status !== 'cancelled' && (
          <div className="order-fulfillments">
            <h3>배송 내역</h3>

            <ul className="fulfillment-actions">
              <li>
                {props.order.receivedAt && (
                  <Button className="button-tiny mark-order-as-not-received" onClick={props.onMarkAsNotReceived}>
                    배송 수령 미완료 처리
                  </Button>
                )}
                {!props.order.receivedAt && (
                  <Button className="button-tiny mark-order-as-received" onClick={props.onMarkAsReceived}>
                    배송 수령 완료 처리
                  </Button>
                )}
              </li>
            </ul>

            {!props.order.fulfillments.length && (
              <p className="no-fulfillments">배송 내역이 없습니다.</p>
            )}

            {!!props.order.fulfillments.length && (
              <table className="table order-fulfillment-list">
                <thead>
                  <tr>
                    <th>배송 상태</th>
                    <th>배송 품목</th>
                  </tr>
                </thead>

                <tbody>
                  {props.order.fulfillments.map((fulfillment, index) => (
                    <tr
                      key={fulfillment._id}
                      className="order-fulfillment"
                      data-fulfillment={fulfillment._id}
                    >
                      <td>
                        <span
                          className="tag fulfillment-status"
                          data-status={fulfillment.status}
                        >
                          {statusTrans(camelCase(['fulfillmentStatus', fulfillment.status]))}
                        </span>
                      </td>
                      <td>
                        <ul className="fulfillment-items">
                          {fulfillment.items.map((item) => (
                            <li key={item.id}>
                              <span className="fulfillment-item-name">
                                {item.item.product.name}
                              </span>
                              <span className="fulfillment-item-quantity">
                                {item.quantity.raw}
                              </span>
                            </li>
                          ))}
                        </ul>

                        <div className="fulfillment-tracker">
                          {fulfillmentTracker(fulfillment)}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
          )}
      </div>
    </div>
  </div>
);

export default OrderDetailTemplate;



// WEBPACK FOOTER //
// ./src/components/order/OrderDetailTemplate/OrderDetailTemplate.js